import type { AxiosInstance } from "axios";
import { ArrowDownToLine, Calendar, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";

import { DateRangePicker } from "../components/DateRangePicker.js";
import { FormulaList, HighlightCard, HighlightGrid, noteText } from "../components/ReportGuide/GuideContent.js";
import { ReportGuideAccordion } from "../components/ReportGuide/ReportGuideAccordion.js";
import { ReportGuideToggleButton } from "../components/ReportGuide/ReportGuideToggleButton.js";
import { Button } from "../components/ui/button.js";
import { InfoTooltip } from "../components/ui/info-tooltip.js";
import type { DateRange } from "../lib/dates.js";

export interface NetworkUserGrowthRow {
  readonly date: string;
  readonly total_users: number;
  readonly new_users: number;
  readonly total_customers: number;
  readonly new_customers: number;
}

export interface NetworkUserGrowthTotals {
  readonly total_users: number;
  readonly new_users: number;
  readonly total_customers: number;
  readonly new_customers: number;
}

export interface NetworkUserGrowthResponse {
  readonly from: string;
  readonly to: string;
  readonly totals: NetworkUserGrowthTotals;
  readonly rows: readonly NetworkUserGrowthRow[];
}

function formatDateShort(dateStr: string): string {
  try {
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      const monthIdx = parseInt(parts[1] ?? "1", 10) - 1;
      const day = parseInt(parts[2] ?? "1", 10);
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return `${day} ${months[monthIdx] || ""}`;
    }
  } catch (error) {
    // A malformed date string is returned AS-IS below — the defined fallback. Bound rather
    // than swallowed per .ai/02 §Errors; there is nothing to report for a formatter.
    void error;
  }
  return dateStr;
}

function formatNumber(val: number): string {
  return new Intl.NumberFormat("en-IN").format(val);
}

export function NetworkUserGrowthScreen({
  client,
  range,
  onRangeChange,
}: {
  readonly client: AxiosInstance;
  readonly range: DateRange;
  readonly onRangeChange: (next: DateRange) => void;
}): React.JSX.Element {
  const [data, setData] = useState<NetworkUserGrowthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [guideOpen, setGuideOpen] = useState(false);

  const fetchData = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await client.get<{
        success: boolean;
        message: string;
        data: NetworkUserGrowthResponse;
      }>("/api/v2/network/user-growth-trend", {
        params: {
          from: range.from,
          to: range.to,
        },
      });
      if (res.data?.success && res.data.data) {
        setData(res.data.data);
      } else {
        setError("Failed to fetch user growth data");
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Error fetching network user growth data";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchData();
  }, [client, range.from, range.to]);

  const handleExportCsv = (): void => {
    if (!data || data.rows.length === 0) return;
    const headers = ["Date", "Total Users", "New Users", "Total Customers", "Total New Customers"];
    const csvRows = [headers.join(",")];

    for (const r of data.rows) {
      csvRows.push([
        `"${formatDateShort(r.date)}"`,
        r.total_users,
        r.new_users,
        r.total_customers,
        r.new_customers,
      ].join(","));
    }

    csvRows.push([
      `"Overall Totals"`,
      data.totals.total_users,
      data.totals.new_users,
      data.totals.total_customers,
      data.totals.new_customers,
    ].join(","));

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `user_growth_${range.from}_to_${range.to}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="flex flex-col gap-4 w-full max-w-[1400px] mx-auto pb-2">
      {/* Top Header & Controls */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-b pb-3">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl font-bold tracking-tight text-foreground font-neulis flex items-center gap-2">
              User Growth Analysis
              <ReportGuideToggleButton
                showGuide={guideOpen}
                onToggle={() => setGuideOpen((v) => !v)}
              />
            </h1>
          </div>
          <p className="text-muted-foreground text-sm mt-1">
            Day-wise cross-brand user acquisition and repeat buyer intelligence across all onboarded stores.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <DateRangePicker value={range} onApply={onRangeChange} />

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleExportCsv}
            disabled={loading || !data || data.rows.length === 0}
            className="h-9 gap-1.5 cursor-pointer shadow-xs"
          >
            <ArrowDownToLine className="size-3.5" />
            <span>Export CSV</span>
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => void fetchData()}
            disabled={loading}
            className="h-9 w-9 p-0 cursor-pointer"
            title="Refresh data"
          >
            <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Guide Accordion (Click to view Report Guide & Logic / Hide Guide & Logic) */}
      <ReportGuideAccordion
        showGuide={guideOpen}
        whatTitle="What this report shows"
        whatBadge="Guide"
        whatContent={
          <>
            <p className={noteText}>
              This report provides a day-by-day cross-brand analysis of visitor acquisition and purchasing intelligence across all onboarded stores in the platform.
            </p>
            <HighlightGrid cols={4}>
              <HighlightCard tone="blue" title="Total Users">
                Total unique visitors visiting any onboarded brand on that date. Deduplicated so a visitor browsing multiple stores on the same day is counted once.
              </HighlightCard>
              <HighlightCard tone="emerald" title="New Users">
                First-time visitors whose very first session across ANY onboarded brand in the network was recorded on this date.
              </HighlightCard>
              <HighlightCard tone="purple" title="Total Customers">
                Total distinct buyers who placed a confirmed order on this date across any onboarded brand in the network.
              </HighlightCard>
              <HighlightCard tone="amber" title="Total New Customers">
                First-time buyers whose very first order across the entire network was placed on this date.
              </HighlightCard>
            </HighlightGrid>
          </>
        }
        howTitle="How metrics & calculations work"
        howBadge="Logic"
        howContent={
          <FormulaList>
            <li>
              <strong>Total Users (Daily Deduplication):</strong> Count of unique <code>visitor_id</code>s recorded across all store sessions on that calendar day (<code>uniqExact(visitor_id)</code>).
            </li>
            <li>
              <strong>New Users (Network Discovery):</strong> Calculated by determining the earliest recorded session timestamp (<code>min(started_at)</code>) across the entire platform history. If that earliest date equals the report row date, the user is classified as a New User.
            </li>
            <li>
              <strong>Total Customers (Daily Purchasers):</strong> Count of distinct buyers (resolved via <code>customer_email_hash</code>, <code>customer_phone_hash</code>, or <code>visitor_id</code>) who placed a non-cancelled order on that day.
            </li>
            <li>
              <strong>Total New Customers (First-Time Buyers):</strong> Count of distinct buyers whose earliest non-cancelled order across the entire network was placed on that calendar day.
            </li>
          </FormulaList>
        }
      />

      {/* Main Report Table Container with 10 visible rows & sticky Overall Totals */}
      <div className="bg-card rounded-xl border border-border shadow-xs overflow-hidden">
        {/* Table Subheader */}
        <div className="px-5 py-3.5 border-b bg-muted/20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="size-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold text-foreground">
              Day-by-Day User Acquisition & Customer Growth
            </h2>
          </div>
          <span className="text-xs text-muted-foreground">
            Showing <strong className="text-foreground">{data?.rows.length ?? 0}</strong> days
          </span>
        </div>

        {error ? (
          <div className="p-8 text-center flex flex-col items-center justify-center gap-3">
            <p className="text-sm text-destructive">{error}</p>
            <Button size="sm" variant="outline" onClick={() => void fetchData()}>
              Retry
            </Button>
          </div>
        ) : loading ? (
          <div className="p-6 space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-10 bg-muted/40 animate-pulse rounded-md" />
            ))}
          </div>
        ) : !data || data.rows.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">
            No user activity found in this date range.
          </div>
        ) : (
          /*
           * Exactly 10 rows visible height (~440px for rows + header + footer)
           * Header sticky at top (sticky top-0 z-20)
           * Overall totals footer sticky at bottom (sticky bottom-0 z-20)
           * Table body in between scrolls smoothly
           */
          <div className="max-h-[min(calc(100dvh-250px),36rem)] overflow-y-auto overflow-x-auto relative overscroll-contain">
            <table className="w-full text-sm text-left border-collapse">
              <thead className="sticky top-0 z-20 bg-muted/95 backdrop-blur-xs">
                <tr className="border-b border-border text-xs font-semibold text-muted-foreground uppercase tracking-wider shadow-2xs">
                  <th className="py-3 px-5 text-left w-[20%] font-semibold bg-muted/95">
                    <div className="inline-flex items-center gap-1.5">
                      <span>Date</span>
                      <InfoTooltip text="Day of user and order activity." />
                    </div>
                  </th>
                  <th className="py-3 px-5 text-right w-[20%] font-semibold bg-muted/95">
                    <div className="inline-flex items-center justify-end gap-1.5 w-full">
                      <span>Total Users</span>
                      <InfoTooltip text="Unique visitors on this date across all onboarded brands. Deduplicated so a user visiting multiple brands is counted once." />
                    </div>
                  </th>
                  <th className="py-3 px-5 text-right w-[20%] font-semibold bg-muted/95">
                    <div className="inline-flex items-center justify-end gap-1.5 w-full">
                      <span>New Users</span>
                      <InfoTooltip text="Visitors whose very first recorded session across any onboarded brand in the network occurred on this date." />
                    </div>
                  </th>
                  <th className="py-3 px-5 text-right w-[20%] font-semibold bg-muted/95">
                    <div className="inline-flex items-center justify-end gap-1.5 w-full">
                      <span>Total Customers</span>
                      <InfoTooltip text="Total distinct buyers who placed a confirmed order on this date across any onboarded brand." />
                    </div>
                  </th>
                  <th className="py-3 px-5 pr-6 text-right w-[20%] font-semibold bg-muted/95">
                    <div className="inline-flex items-center justify-end gap-1.5 w-full">
                      <span>Total New Customers</span>
                      <InfoTooltip text="First-time buyers whose very first order across the network was placed on this date." />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {data.rows.map((row) => (
                  <tr
                    key={row.date}
                    className="hover:bg-accent/40 transition-colors group h-11"
                  >
                    <td className="py-2.5 px-5 font-medium text-foreground whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="text-foreground font-semibold">{formatDateShort(row.date)}</span>
                        <span className="text-xs text-muted-foreground hidden sm:inline">
                          ({row.date})
                        </span>
                      </div>
                    </td>
                    <td className="py-2.5 px-5 text-right font-medium text-foreground tabular-nums">
                      {formatNumber(row.total_users)}
                    </td>
                    <td className="py-2.5 px-5 text-right font-medium text-foreground tabular-nums">
                      <span className="inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 font-semibold">
                        {formatNumber(row.new_users)}
                      </span>
                    </td>
                    <td className="py-2.5 px-5 text-right font-medium text-foreground tabular-nums">
                      <span className="inline-flex items-center gap-1.5 text-purple-600 dark:text-purple-400 font-semibold">
                        {formatNumber(row.total_customers)}
                      </span>
                    </td>
                    <td className="py-2.5 px-5 pr-6 text-right font-medium text-foreground tabular-nums">
                      <span className="inline-flex items-center gap-1.5 text-amber-600 dark:text-amber-400 font-semibold">
                        {formatNumber(row.new_customers)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="sticky bottom-0 z-20 bg-muted/95 backdrop-blur-xs">
                <tr className="border-t-2 border-border font-bold text-foreground shadow-2xs">
                  <td className="py-3.5 px-5 font-bold uppercase tracking-wider text-xs bg-muted/95">
                    Overall Totals
                  </td>
                  <td className="py-3.5 px-5 text-right tabular-nums text-base bg-muted/95">
                    {formatNumber(data.totals.total_users)}
                  </td>
                  <td className="py-3.5 px-5 text-right tabular-nums text-base text-emerald-600 dark:text-emerald-400 bg-muted/95">
                    {formatNumber(data.totals.new_users)}
                  </td>
                  <td className="py-3.5 px-5 text-right tabular-nums text-base text-purple-600 dark:text-purple-400 bg-muted/95">
                    {formatNumber(data.totals.total_customers)}
                  </td>
                  <td className="py-3.5 px-5 pr-6 text-right tabular-nums text-base text-amber-600 dark:text-amber-400 bg-muted/95">
                    {formatNumber(data.totals.new_customers)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
